import {Pipe, PipeTransform} from '@angular/core';
import {DatePipe} from '@angular/common';
import {APP_LOCALE_ID} from '../app-locale';
import {HelperModels} from '../models/helper-models';

@Pipe({
    name: 'datumFormat'
})
export class DatumFormatPipe extends DatePipe implements PipeTransform {

    private weekday = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];

    // Konvertiert ein Datum in einen String
    transform(value: any, args?: any): any {
        if (!value) {
            return '';
        }
        if (args) {
            return super.transform(value, args, APP_LOCALE_ID);
        } else {
            return super.transform(value, 'dd.MM.yyyy HH:mm', APP_LOCALE_ID);
        }
    }

    transformEN(value: any, args?: any): any {
        if (args) {
            return super.transform(value, args, APP_LOCALE_ID);
        } else {
            return super.transform(value, 'yyyy-MM-ddTHH:mm:ss', APP_LOCALE_ID);
        }
    }

    transformDateWithTime(value: any, args?: any): any {
        if (args) {
            return super.transform(value, args, APP_LOCALE_ID);
        } else {
            return super.transform(value, 'dd.MM.yyyy HH:mm', APP_LOCALE_ID);
        }
    }

    transformOnlyDate(value: any, args?: any): any {
        return super.transform(value, 'dd.MM.yyyy', APP_LOCALE_ID);
    }

    transformOnlyDateSmallYear(value: any, args?: any): any {
        return super.transform(value, 'dd.MM.yy', APP_LOCALE_ID);
    }

    transformOnlyTime(value: any, args?: any): any {
        return super.transform(value, 'HH:mm', APP_LOCALE_ID);
    }

    transformOnlyDateEN(value: any, args?: any): any {
        return super.transform(value, 'yyyy-MM-dd', APP_LOCALE_ID);
    }

    // Gibt das heutige Datum im Format dd.MM.yyyy zurück
    getTodaySwissFormat(): string {
        return this.transformOnlyDate(new Date());
    }

    // Liefert ein Datum in folgendem Format zurück: Montag, 21.11.2022
    getWeekdayAndSwissDate(value: Date): string {
        return this.weekday[value.getDay()] + ', ' + this.transformOnlyDate(value);
    }

    // Konvertiert ein Datum im Format yyyy-MM-dd in das Format dd.MM.yyyy in
    convertDateStringEN2DateStringCH(value: string): string {
        let year = value.substring(0, 4);
        let month = value.substring(5, 7);
        let day = value.substring(8, 10);
        return day + '.' + month + '.' + year;
    }

    // Konvertiert ein Datum im Format dd.MM.yyyy in ein Date
    convertStringToDate(value: string): Date {
        let year = Number(value.substring(6, 10));
        let month = Number(value.substring(3, 5)) - 1;
        let day = Number(value.substring(0, 2));
        let datum = new Date(year, month, day, 0, 0, 0);
        return datum;
    }

    // Konvertiert ein EN-Datum im Format yyyy-MM-ddThh:mm:ss in ein Date
    convertStringENToDate(value: string): Date {
        let year = Number(value.substring(0, 4));
        let month = Number(value.substring(5, 7)) - 1;
        let day = Number(value.substring(8, 10));
        let datum = new Date(year, month, day, 0, 0, 0);
        return datum;
    }

    // Macht aus einem Datum einen Datumsbereich
    // Eingangswert als String: 16.03.2022
    // Rückgabeobjekt: von 16.03.2022 00:00:00 als Datum
    //                 bis 16.03.2022 23:59:59 als Datum

    getDayRangeFromDate(value: string): HelperModels.DatumRange {
        let year = Number(value.substring(6, 10));
        let month = Number(value.substring(3, 5)) - 1;
        let day = Number(value.substring(0, 2));
        return {
            von: new Date(year, month, day, 0, 0, 0), bis: new Date(year, month, day, 23, 59, 59)
        };
    }

    // Entfernt aus einem Datum die Uhrzeit und sendet ein Datum zurück
    removeTimeFromDate(date: Date): Date {
        return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0);
    }


    /**
     * return a date on the same Date as supplied Date setting the time as supplied time
     * @param date a date that will be used as the base date for the new date
     * @param time in format HH:mm:ss.ssss, if any part of the values is missing eg HH:mm is given 0 will be used for the other values
     * @throws Error when formatting is wrong
     */
    addTimeToDate(date: Date, time: string): Date {
        const returnDate = new Date(date);
        try {
            const splitTimeValues: number[] = [];
            time.split(':').forEach(t => {
                const n = Number.parseInt(t);
                if (Number.isNaN(n)) {
                    const arr = t.split('.');
                    if (arr.length != 2) {
                        throw new Error('Bad Formatting of TimeString');
                    }
                    splitTimeValues.push(Number.parseInt(arr[0]));
                    splitTimeValues.push(Number.parseInt(arr[1]));

                } else {
                    splitTimeValues.push(n);
                }
            });
            returnDate.setHours(splitTimeValues[0] || 0, splitTimeValues[1] || 0, splitTimeValues[2] || 0, splitTimeValues[3] || 0);
        } catch {
            throw Error('Bad Formatting of TimeString');
        }
        return returnDate;
    }


    // Parameter-Format 2023-20-04T14:00:00
    ouersBetweenToStringDates(start: Date, end: Date): number {
        return this.ouersBetweenToDates(new Date(start), new Date(end));
    }

    ouersBetweenToDates(start: Date, end: Date): number {
        var hours = Math.abs(end.valueOf() - start.valueOf()) / 36e5;
        // Auf 2 Stellen nach dem Komma runden
        var rounded = Math.round(hours * 100) / 100;
        return rounded;
    }

    // Liefert aus eine Datum folgendes zurück:
    // yyyyMMddT000000Z
    // wird für Terminserien/Terminserie-Ausnahmen benutzt
    getSyncfusionCalendarDateStr(datum: Date): string {
        let month: string = (datum.getMonth() + 1).toString();
        if (month.length == 1) {
            month = '0' + month;
        }
        let day: string = datum.getDate().toString();
        if (day.length == 1) {
            day = '0' + day;
        }
        return datum.getFullYear().toString() + month + day + 'T000000';
    }

}


